'use client';

import favoriteIconActive from '@/assets/images/icons/icon-favorite-active.png';
import favoriteIcon from '@/assets/images/icons/icon-favorite.png';
import FaIcon from '@/components/icon/FaIcon/FaIcon';
import SignInPromptModal from '@/components/layouts/SignInPromptModal/SignInPromptModal';
import ImageDetailModal from '@/components/ui/ImageDetailModal/ImageDetailModal';
import ModalBase from '@/components/ui/ModalBase/ModalBase';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import ProgressAnime from '@/components/ui/ProgressAnime/ProgressAnime';
import RoundArrowIcon from '@/components/ui/RoundArrowIcon/RoundArrowIcon';
import RoundIcon from '@/components/ui/RoundIcon/RoundIcon';
import RoundTextIcon from '@/components/ui/RoundTextIcon/RoundTextIcon';
import { SALE_STATUS, UNAVAILABLE_SALE_STATUS_LABEL } from '@/constants';
import { useAuthUser } from '@/hooks/useAuthUser';
import { swal } from '@/lib/sweetalert';
import { useGlobalLoadingAtom } from '@/state';
import { BadgeStatus } from '@/types';
import { formatCurrency } from '@/utils/formatters';
import { pickErrorMessages } from '@/utils/functions';
import { sendSelectItemEvent, sendViewItemListEvent } from '@/utils/ga';
import { buildProductRoute } from '@/utils/worksRoute';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { deleteFavoriteMaterial } from '../../api/deleteFavoriteMaterial';
import { postFavoriteMaterial } from '../../api/postFavoriteMaterial';
import { useMaterialDetail } from '../../hooks/useMaterialDetail';
import {
  type MaterialSequenceApiParams,
  type MaterialSequenceInitialState,
  useMaterialSequence,
} from '../../hooks/useMaterialSequence';
import type { GfMaterialDetail } from '../../types';
import { groupProductsByCategory } from '../../utils/groupProductsByCategory';
import MaterialDetailSlider, { type MaterialDetailSliderRef } from '../MaterialDetailSlider/MaterialDetailSlider';
import styles from './MaterialDetailModal.module.scss';

/**
 * コマ詳細モーダル。開いたコマから前後のコマへスワイプ/矢印で送れる。
 * 呼び出し側で「開いたコマ ID」を key に指定して再マウントする前提で、initialState から並びを組み立てる。
 * 前後送りは useMaterialSequence、表示中コマの利用可能商品は useMaterialDetail に分けて持つ。
 */
function MaterialDetailModal({
  isOpen,
  onClose,
  initialState,
  apiParams,
  initialDetail,
  filteredProductId,
  backgroundColor,
  onFavoriteChanged,
}: {
  isOpen: boolean;
  onClose: () => void;
  initialState: MaterialSequenceInitialState;
  /** 前後のコマを読み足すための条件。読み足さない固定リスト（お気に入り一覧）では省略する */
  apiParams?: MaterialSequenceApiParams;
  /** 開いた時点で詳細を持っている場合（一覧クリック・URL 直開き）に渡す。再フェッチを避ける */
  initialDetail?: GfMaterialDetail;
  /** アイテムフィルターで絞り込み中の商品ID。遷移先から戻ったときにフィルターを復元するために引き継ぐ */
  filteredProductId?: number;
  backgroundColor?: string;
  /** お気に入り変更を一覧側（キャッシュ・計測）へ反映する */
  onFavoriteChanged: (materialId: number, isFavorite: boolean) => void;
}) {
  const { items, currentIndex, isPrevDisabled, isNextDisabled, sliderKey, handleIndexChange, updateFavoriteInCache } =
    useMaterialSequence(initialState, apiParams);
  const {
    detail,
    isDetailLoading,
    isDetailError,
    loadDetail,
    provideDetail,
    updateFavoriteInCache: updateDetailFavoriteInCache,
  } = useMaterialDetail();

  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);
  const [signInPromptModalOpen, setSignInPromptModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  // スワイプのドラッグ終了時に背景クリックが誤発火してモーダルが閉じるのを防ぐ
  const [isDragging, setIsDragging] = useState(false);
  const { isLoggedIn } = useAuthUser();
  const router = useRouter();
  const [, startGlobalLoading] = useGlobalLoadingAtom();
  const sliderRef = useRef<MaterialDetailSliderRef>(null);

  const currentItem = items[currentIndex];
  // 名前（「9巻88話目」等）は一覧データにあるので即時表示。詳細フェッチを待たない。
  const displayName = currentItem?.name ?? '';
  // 拡大表示の高解像度画像は、詳細が表示中コマと一致するときだけ使う。
  // 送り直後の取得待ちでは detail が前のコマのままなので、一致しない間はサムネにフォールバックする。
  const isDetailForCurrentItem = detail?.id === currentItem?.id;
  const zoomImageUrl = (isDetailForCurrentItem ? detail?.imagePath : undefined) ?? currentItem?.thumbnailPath ?? '';

  // 開いた時点のコマの詳細を用意する。すでに持っていればそれを使い、無ければ取得する。key 再マウント前提で 1 回だけ。
  useEffect(() => {
    if (initialDetail) {
      provideDetail(initialDetail);
    } else if (currentItem) {
      loadDetail(currentItem.id);
    }
    // マウント時に開いたコマぶんだけ実行する（以降の送りは handleSlideChange で取得）
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 開いた／別コマへ送るたびに、そのコマの利用可能アイテムで view_item_list を発火する。
  // お気に入りトグルでも detail は作り直されるが、表示コマは変わらないので id で判定して二重送信を防ぐ。
  useEffect(() => {
    if (!detail) return;
    const gaItems = detail.availableProducts.map((product) => ({
      item_id: product.id.toString(),
      item_name: product.name,
      item_brand: detail.work.name || '',
      material_ids: detail.id.toString(),
    }));
    sendViewItemListEvent(gaItems, 'コマ詳細モーダル - 利用可能なアイテム');
  }, [detail?.id]);

  const handleSlideChange = (index: number) => {
    const material = items[index];
    if (material) loadDetail(material.id);
    handleIndexChange(index);
  };

  const handleToggleFavorite = async (isFavorite: boolean, materialId: number) => {
    try {
      setIsFavoriteLoading(true);
      if (!isLoggedIn) {
        setSignInPromptModalOpen(true);
        return;
      }
      if (isFavorite) {
        await deleteFavoriteMaterial({ gfMaterialId: materialId });
      } else {
        await postFavoriteMaterial({ gfMaterialId: materialId });
      }
      const nextFavorite = !isFavorite;
      updateFavoriteInCache(materialId, nextFavorite);
      updateDetailFavoriteInCache(materialId, nextFavorite);
      onFavoriteChanged(materialId, nextFavorite);
      router.refresh();
    } catch (error) {
      await swal.messages({ messages: pickErrorMessages(error) });
    } finally {
      setIsFavoriteLoading(false);
    }
  };

  const handleProductClick = (productId: number, materialId: number) => {
    if (!detail) return;
    const url = buildProductRoute({ productId, materialId, workId: detail.work.id, filteredProductId });
    startGlobalLoading();

    // GAイベント送信（router.pushより前に発火し、遷移による取りこぼしを防止）
    const product = detail.availableProducts.find((p) => p.id === productId);
    if (product) {
      sendSelectItemEvent([
        {
          item_id: productId.toString(),
          item_name: product.name,
          item_brand: detail.work.name || '',
          material_ids: detail.id.toString(),
        },
      ]);
    }

    router.push(url);
  };

  // 利用可能アイテム欄の出し分け: ローディング / 取得失敗 / 一覧 / なし。
  // 即オープン時はここが取得失敗の唯一の通知経路になるため、失敗は無言にせずメッセージを出す。
  const renderAvailableProducts = () => {
    if (isDetailLoading) {
      return (
        <div className={styles.productLoading}>
          <ProgressAnime />
        </div>
      );
    }
    if (isDetailError) {
      return (
        <div className={styles.productLoading}>
          <p className={styles.productLoadError}>
            アイテム情報を取得できませんでした。時間をおいて再度お試しください。
          </p>
        </div>
      );
    }
    if (!detail || detail.availableProducts.length === 0) return null;
    return (
      <div className={styles.categoryGroups}>
        {groupProductsByCategory(detail.availableProducts).map((group) => (
          <div key={group.categoryName} className={styles.categoryGroup}>
            <h4 className={styles.categoryGroupTitle}>{group.categoryName}</h4>
            <hr className={styles.categoryGroupDivider} />
            <div className={styles.categoryGroupProducts}>
              {group.products.map((product) => {
                // ラベル導出と narrowing を兼ねるため、判定は saleStatus の直接比較で行う
                const unavailableLabel =
                  product.saleStatus === SALE_STATUS.onSale ? null : UNAVAILABLE_SALE_STATUS_LABEL[product.saleStatus];
                const isOnSale = unavailableLabel === null;
                return (
                  <button
                    className={styles.productRowButton}
                    type='button'
                    onClick={() => handleProductClick(product.id, detail.id)}
                    key={product.id}
                    disabled={!isOnSale}
                    data-testid='product-selection-button'
                  >
                    <div
                      className={`${styles.productRow} ${isOnSale ? styles.productRowActive : styles.productRowDisabled}`}
                    >
                      <div className={styles.productRowContentWrapper}>
                        <NextImageView
                          src={product.thumbnailPaths.sub}
                          alt={product.name}
                          fill={false}
                          width={40}
                          height={40}
                          objectFit='contain'
                          className={styles.productImage}
                        />
                        <div className={styles.productMessageWrapper}>
                          <p className={styles.productMessage}>{product.name}</p>
                          {isOnSale ? (
                            <p className={styles.productPrice}>
                              <span className={styles.productPriceCurrency}>¥</span>
                              {formatCurrency(product.basePrice)}〜＋送料
                            </p>
                          ) : (
                            <p className={styles.productUnavailableLabel}>{unavailableLabel}</p>
                          )}
                        </div>
                      </div>
                      {isOnSale && (
                        <div className={styles.productRowArrowContainer}>
                          <FaIcon icon={faChevronRight} />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={onClose}
      maskBgColor={backgroundColor}
      modalContainerSize='large'
      bgColor='gray100'
      showDefaultCloseButton={true}
      closeButtonBgColor={backgroundColor}
      showDefaultCloseButtonOnlyOnPc={true}
      showBottomCloseButton={true}
      closeOnBackgroundClick={!isDragging}
      pcVerticalAlign='top'
    >
      {!currentItem ? (
        <ProgressAnime />
      ) : (
        <div className={styles.materialDetailModal}>
          <div className={styles.materialDetailContent}>
            <div className={styles.titleWrapper}>
              <div className={styles.materialDetailTitle}>作りたいアイテムを選ぼう</div>
              <div className={styles.materialDetailSubtext}>※アイテムによって使用できないコマがあります。</div>
            </div>

            <div className={styles.materialContent}>
              <div className={styles.sliderArea}>
                <MaterialDetailSlider
                  ref={sliderRef}
                  key={sliderKey}
                  items={items}
                  startIndex={currentIndex}
                  onIndexChange={handleSlideChange}
                  onDraggingChange={setIsDragging}
                  onImageClick={() => setIsImageModalOpen(true)}
                />
                {currentItem.badgeStatus === BadgeStatus.new.value && (
                  <div className={styles.badge} aria-hidden='true'>
                    <RoundTextIcon bgColor='orange' text={BadgeStatus.new.text} size='defaultFixed' />
                  </div>
                )}
                <button
                  className={styles.favoriteButton}
                  type='button'
                  onClick={() => handleToggleFavorite(currentItem.isFavorite, currentItem.id)}
                  disabled={isFavoriteLoading}
                  aria-label={currentItem.isFavorite ? 'お気に入りを解除' : 'お気に入りに登録'}
                >
                  <RoundIcon
                    imgSrc={currentItem.isFavorite ? favoriteIconActive.src : favoriteIcon.src}
                    size='defaultFixed'
                    bgColor='white'
                  />
                </button>
                <div className={styles.navSection}>
                  <button
                    type='button'
                    className={clsx(styles.navButton, styles.navButtonPrev)}
                    onClick={() => sliderRef.current?.goPrev()}
                    disabled={isPrevDisabled}
                    aria-label='前のコマ'
                  >
                    <RoundArrowIcon
                      direction='left'
                      color='black'
                      size='large'
                      sizePc='medium'
                      bgColor='white'
                      isBold
                    />
                  </button>
                  <button
                    type='button'
                    className={clsx(styles.navButton, styles.navButtonNext)}
                    onClick={() => sliderRef.current?.goNext()}
                    disabled={isNextDisabled}
                    aria-label='次のコマ'
                  >
                    <RoundArrowIcon
                      direction='right'
                      color='black'
                      size='large'
                      sizePc='medium'
                      bgColor='white'
                      isBold
                    />
                  </button>
                </div>
              </div>
            </div>

            <p className={styles.materialName}>{displayName}</p>

            <div className={styles.productContent}>{renderAvailableProducts()}</div>
          </div>
        </div>
      )}
      <SignInPromptModal
        isOpen={signInPromptModalOpen}
        onClose={() => setSignInPromptModalOpen(false)}
        description='コマをお気に入りに登録するには、ログインが必要です。'
      />
      <ImageDetailModal isOpen={isImageModalOpen} onClose={() => setIsImageModalOpen(false)} imageUrl={zoomImageUrl} />
    </ModalBase>
  );
}

export default MaterialDetailModal;
